import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { Pencil } from 'lucide-react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  canActivateMtr,
  canDeactivateMtr,
} from '#/modules/imsMgmt/settings/mtr/helper/mtrFilterOptions';
import useMtrStatusMutation, {
  MTR_ACTIVATE_CONFIRM,
  MTR_DEACTIVATE_CONFIRM,
} from '#/modules/imsMgmt/settings/mtr/hooks/useMtrStatusMutation';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';

const MtrCommandBar = ({ detail, onRefetch }) => {
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const canUpdate = useCan(PERM.MTR_UPDATE);
  const canActivatePerm = useCan(PERM.MTR_ACTIVATE);

  const id = detail?.id;
  const status = detail?.status;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;
  const canActivate = canActivatePerm && canActivateMtr(status);
  const canDeactivate = canActivatePerm && canDeactivateMtr(status);

  const statusMutation = useMtrStatusMutation({
    id,
    getUpdatedAt: () => updatedAt,
    onAfterSuccess: async () => {
      await onRefetch?.();
    },
    onVersionConflict: () => {
      onRefetch?.();
    },
  });

  const isPending = statusMutation.isPending;

  async function handleActivate() {
    const ok = await confirm(MTR_ACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'activate' });
  }

  async function handleDeactivate() {
    const ok = await confirm(MTR_DEACTIVATE_CONFIRM);
    if (!ok) {
      return;
    }
    statusMutation.mutate({ action: 'deactivate' });
  }

  return (
    <>
      {ConfirmDialogHost}
      {canUpdate ? (
        <Button
          asChild
          variant="default"
          className={cn('document-detail-edit-action')}
        >
          <Link to={ROUTES.mtrEdit(id)} aria-label="編輯">
            <Pencil className="size-3.5" aria-hidden />
            編輯
          </Link>
        </Button>
      ) : null}
      {canActivate ? (
        <Button disabled={isPending} onClick={handleActivate}>
          啟用
        </Button>
      ) : null}
      {canDeactivate ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={handleDeactivate}
        >
          停用
        </Button>
      ) : null}
    </>
  );
};

MtrCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default MtrCommandBar;
